<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2023-03-08 17:24:40
 * @LastEditors: 龙兆柒
-->
<script setup lang="ts">
import { h, nextTick, onMounted, reactive, ref, shallowRef, useAttrs, watch, watchEffect } from 'vue';
import type { RowProps } from 'ant-design-vue';
import { Button, Drawer, TableColumn, Tag, Spin, Popconfirm, Tooltip } from 'ant-design-vue';
import {
  ControlOutlined,
  DeleteOutlined,
  DragOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  SyncOutlined,
  RestOutlined,
} from '@ant-design/icons-vue';
import type { Key } from 'ant-design-vue/lib/table/interface';
import { get } from 'lodash-es';
import Sortable from 'sortablejs';
import dayjs from 'dayjs';
import type { AlignType, GetRowKey } from 'ant-design-vue/lib/vc-table/interface';
import type { TableBaseProps } from '../../table/types';
import { SearchForm } from '../../search-form';
import FormItem from '../../pro-form/src/_FormItem.vue';
import { Table } from '../../table';
import { Switch } from '../../switch';
import type { ColumnProps, RecordType, Scope, _SearchField, _TableRowSelection } from '../types';
import { deepCopy } from '../../util';
import type { FieldKeys } from '../../search-form/types';
import type { FormBaseField } from '../../pro-form/types';

const props = withDefaults(
  defineProps<{
    columns: ColumnProps<RecordType>[];
    align?: AlignType;
    isRowEdit?: boolean;
    isCatRecord?: boolean;
    searchForm?: { [key: string]: any };
    collapsed?: boolean;
    showSearch?: boolean;
    operation?: boolean;
    operationConfig?: ColumnProps<RecordType>;
    rowSelection?: object | false;
    showToolBtn?: boolean;
    hidePrint?: boolean;
    hideHeader?: boolean;
    showAddBtn?: boolean;
    showDelBtn?: boolean;
    labelAlign?: 'left' | 'right';
    labelWrap?: boolean;
    labelCol?: RowProps;
    /* 显示的字段 */
    showFields?: FieldKeys;
    /* 隐藏的字段, 优先级高。与showFields互斥使用，优先使用hideFields */
    hideFields?: FieldKeys;
    serialNumber?: boolean;
    serialNumberConfig?: ColumnProps<RecordType>;
    rowKey: string | GetRowKey<any>;
    showRecycle?: boolean;
  }>(),
  {
    columns: () => [],
    searchForm: () => ({}),
    showSearch: true,
    operation: false,
    operationConfig: () => ({ width: '200px' }),
    rowSelection: undefined,
    showToolBtn: true,
    showAddBtn: false,
    showDelBtn: true,
    hideHeader: false,
    labelAlign: 'right',
    labelWrap: false,
    serialNumber: false,
    showRecycle: false,
    serialNumberConfig: () => ({ width: '70px' }),
  }
);

const emits = defineEmits<{
  (event: 'addRow'): void;
  (event: 'delRows', ids?: Key[], rows?: RecordType[]): void;
  (event: 'update:showSearch', val: boolean): void;
  (event: 'update:searchForm', val: { [key: string]: any }): void;
  (event: 'search', form: { [key: string]: any }): void;
  (event: 'reset', form: { [key: string]: any }): void;
  (event: 'refresh'): void;
  /* 查看 */
  (event: 'catRecord', scope: Scope): void;
  (event: 'delRecord', scope: Scope): void;
  (event: 'isRecycle', isRecycle: Boolean): void;
  /* 保存编辑内容 */
  // (event: 'saveEditRecord', record: RecordType): Promise<void>;
}>();

const OPERATION = 'OPERATION';
const SERIAL_NUMBER = 'SERIAL_NUMBER';

const attrs = useAttrs() as TableBaseProps & Record<string, any>;

const searchFields = ref<_SearchField[]>([]);

const _searchForm = ref<{ [key: string]: any }>({ ...props.searchForm });

const _isShowSearch = ref(true);

const isRecycle = ref(false);

watchEffect(() => {
  _isShowSearch.value = props.showSearch;
});

const _rowSelection = ref<_TableRowSelection | null>({
  onChange(selectedRowKeys, selectedRows) {
    if (_rowSelection.value) {
      _rowSelection.value.selectedRowKeys = selectedRowKeys;
      _rowSelection.value.selectedRows = selectedRows;
    }
  },
});

watchEffect(() => {
  if (props.rowSelection) _rowSelection.value = { ...props.rowSelection };
  if (props.rowSelection === false) _rowSelection.value = null;
});

onMounted(() => {
  if (_rowSelection.value && !props.rowKey) console.error('rowSelection不为false时，rowKey不能为空');
});

const _columns = ref<ColumnProps<RecordType>[]>([]);

function getViewVale(column: ColumnProps<RecordType>, record: RecordType) {
  const key = column.dataIndex || record.key;
  const val = get(record, key);
  if (column.enum && typeof column.enum === 'object') {
    if (Array.isArray(val)) {
      return val.map(e => column.enum[e]);
    }
    return [column.enum[val]];
  }
  return val;
}

function handleSearchField(item: ColumnProps<RecordType>): _SearchField {
  const prop = (Array.isArray(item.dataIndex) ? item.dataIndex[item.dataIndex.length - 1] : item.dataIndex) as string;
  return {
    ...item.search,
    el: item.search!.el,
    label: item?.search?.label || (item.title?.toString() as string),
    prop: item?.search?.prop || prop,
  };
}

/* 排序依据 */
const originalColumn = shallowRef<ColumnProps<RecordType>[]>([]);

function initColumn(callback?: (item: ColumnProps<RecordType>, index: number) => void) {
  _columns.value = [];
  originalColumn.value.forEach((item, index) => {
    if (props.align && !item.align) item.align = props.align;
    if (item?.isShow !== false) _columns.value.push(item);
    if (callback) callback(item, index);
  });
  if (props.operation) {
    _columns.value.push({
      title: '操作',
      dataIndex: OPERATION,
      align: 'center',
      ...props.operationConfig,
    });
  }
  if (props.serialNumber) {
    _columns.value.unshift({
      title: '序号',
      dataIndex: SERIAL_NUMBER,
      align: 'center',
      ...props.serialNumberConfig,
    });
  }
}

watchEffect(() => {
  searchFields.value = [];
  originalColumn.value = deepCopy(props.columns);
  initColumn(item => {
    if (item.search) searchFields.value.push(handleSearchField(item));
  });
});

const tableRef = ref<InstanceType<typeof Table>>();

const postParams = ref<{ [key: string]: any }>({});

const getParams = ref<{ [key: string]: any }>({});

function refresh() {
  if (attrs?.api) tableRef.value?.refreshData(true);
  emits('refresh');
}

function searchSwitch() {
  _isShowSearch.value = !_isShowSearch.value;
  emits('update:showSearch', _isShowSearch.value);
}

function setParams(form: { [key: string]: any }) {
  searchFields.value.forEach(item => {
    if (item?.type === 'GET' && item.prop) getParams.value[item.prop] = form[item.prop];
    else if (item.prop) postParams.value[item.prop] = form[item.prop];
  });
}

function handleSearchMode(form: { [key: string]: any }) {
  emits('update:searchForm', form);
  setParams(form);
}

function search(form: { [key: string]: any }) {
  if (attrs?.api) {
    tableRef.value?.fetchData();
  }
  nextTick(() => {
    emits('search', form);
  });
}

function reset(form: { [key: string]: any }) {
  if (attrs?.api) {
    nextTick(() => {
      tableRef.value?.fetchData();
    });
  }
  nextTick(() => {
    emits('reset', form);
  });
}
function recycle() {
  isRecycle.value = !isRecycle.value;
  emits('isRecycle', isRecycle.value);
}

/* 列 */
const columnSettings = ref(false);

function columnSettingsSwitch() {
  columnSettings.value = !columnSettings.value;
}

function columnSettingsChange(checked: boolean, record: ColumnProps<RecordType>, index: number) {
  if (checked) {
    _columns.value.splice(index, 0, record);
  } else {
    const i = _columns.value.findIndex(e => e.key === record.key || e.dataIndex === record.dataIndex);
    _columns.value.splice(i, 1);
  }
}

const columnTableRef = ref();

watch(columnSettings, val => {
  if (val) {
    nextTick(() => {
      const el = (columnTableRef.value.$el as HTMLElement).querySelector('tbody');
      if (!el) return;
      const res = new Sortable(el, {
        animation: 150,
        filter: '.filter',
        onEnd: ({ newIndex, oldIndex }: any) => {
          originalColumn.value.splice(newIndex, 0, originalColumn.value.splice(oldIndex, 1)[0]);

          _columns.value.sort((item1, item2) => {
            if (item1.dataIndex === OPERATION) return 1;
            if (item1.dataIndex === SERIAL_NUMBER) return -1;
            return originalColumn.value.findIndex(e => e.dataIndex === item1.dataIndex) <=
              originalColumn.value.findIndex(e => e.dataIndex === item2.dataIndex)
              ? -1
              : 1;
          });
        },
      });
    });
  }
});

/* 行编辑 */

const editData = reactive<{
  index: number;
  record: RecordType;
}>({
  index: -1,
  record: null,
});

function catRecord(scope: Scope) {
  emits('catRecord', scope);
}

function isCurrentRow(record: RecordType, index: number) {
  if (!editData.record) return false;
  if (typeof props.rowKey === 'function') {
    return props.rowKey(record, index) === props.rowKey(editData.record, editData.index);
  } else {
    return record[props.rowKey] === editData.record[props.rowKey];
  }
}

function editRecord({ record, index }: Scope): true | string {
  const dataSource = tableRef.value?.dataSource_in;
  if (!dataSource || dataSource.length === 0) throw new Error('数据为空');
  editData.record = record;
  editData.index = index;
  return true;
}

function cancelEditRecord() {
  editData.index = -1;
  editData.record = null;
}

function delRecord(scope: Scope) {
  emits('delRecord', scope);
}

const recordLoading = ref(false);

async function saveEditRecord(fn?: (record: RecordType) => Promise<void>) {
  recordLoading.value = true;
  const dataSource = tableRef.value?.dataSource_in;
  try {
    await (fn != undefined ? fn(editData.record) : attrs?.onSaveEditRecord(editData.record));
    if (dataSource) {
      const data = dataSource.find(isCurrentRow);
      Object.assign(data, deepCopy(editData.record));
    }
    editData.index = -1;
    editData.record = null;
    recordLoading.value = false;
  } catch (err) {
    recordLoading.value = false;
  }
}

function getFormFiled(scope: Scope): FormBaseField {
  if (typeof scope.column.edit === 'object' && !scope.column.edit.el) {
    return scope.column.edit as FormBaseField;
  }
  const getEl = () => {
    const temp: any = {
      el: 'input',
      attrs: {},
    };

    if (scope.column.search) {
      temp.el = scope.column.search.el;
      if (scope.column.search.attrs) {
        Object.keys(scope.column.search.attrs as Record<string, any>).forEach((key: string) => {
          const value = ((scope.column.search?.attrs as any) ?? {})[key];
          if (!(key.startsWith('on') && typeof value === 'function')) temp.attrs[key] = value;
        });
      }
    } else {
      const type = typeof scope.text;
      const el: any = {
        number: 'input-number',
        string: 'input',
        boolean: 'boolean-select',
      };
      if (type in el) temp.el = el[type];
    }

    return temp;
  };

  const prop = (
    Array.isArray(scope.column.dataIndex)
      ? scope.column.dataIndex[scope.column.dataIndex.length - 1]
      : scope.column.dataIndex
  ) as string;

  const field: FormBaseField = {
    label: undefined,
    prop,
    ...(getEl() as any),
  };

  return field;
}

defineExpose({
  table: tableRef,
  search,
  refresh,
  columnSettingsSwitch,
  searchSwitch,
  reset,
  editRecord,
  cancelEditRecord,
  saveEditRecord,
  isRecycle,
});
</script>

<script lang="ts">
export default {
  name: 'ProTable',
  inheritAttrs: false,
};
</script>

<template>
  <SearchForm
    v-if="searchFields.length"
    v-show="_isShowSearch"
    v-model:value="_searchForm"
    class="table-search"
    :fields="searchFields"
    :collapsed="collapsed"
    :label-align="labelAlign"
    :label-wrap="labelWrap"
    :label-col="labelCol"
    :showFields="showFields"
    :hideFields="hideFields"
    @update:value="handleSearchMode"
    @search="search"
    @reset="reset"
  />
  <div class="table-main">
    <div class="table-header">
      <div class="header-button-lf">
        <slot name="header" v-bind="{ rowSelection: _rowSelection }">
          <template v-if="hideHeader === false">
            <Button v-if="showAddBtn" type="primary" @click="emits('addRow')"> <PlusCircleOutlined />添加 </Button>
            <Button
              v-if="showDelBtn && _rowSelection"
              type="primary"
              danger
              :disabled="!_rowSelection?.selectedRowKeys?.length"
              @click="emits('delRows', _rowSelection?.selectedRowKeys, _rowSelection?.selectedRows)"
            >
              <DeleteOutlined />批量删除
            </Button>
          </template>
        </slot>
      </div>
      <div v-if="showToolBtn" class="header-button-ri">
        <slot name="toolButton" v-bind="{ rowSelection: _rowSelection, columns: originalColumn }">
          <Tooltip title="刷新" style="right: 50px">
            <Button v-if="attrs?.api" shape="circle" @click="refresh">
              <SyncOutlined />
            </Button>
          </Tooltip>
          <Tooltip title="列设置" style="right: 50px">
            <Button shape="circle" @click="columnSettingsSwitch">
              <ControlOutlined />
            </Button>
          </Tooltip>
          <Tooltip title="搜索栏" style="right: 50px">
            <Button v-if="searchFields.length" shape="circle" @click="searchSwitch">
              <SearchOutlined />
            </Button>
          </Tooltip>
          <Tooltip v-if="props.showRecycle" title="回收站" style="right: 50px">
            <Button v-if="!isRecycle" shape="circle" :icon="h(RestOutlined)" @click="recycle"></Button>
            <Button v-else danger shape="circle" :icon="h(RestOutlined)" @click="recycle"></Button>
          </Tooltip>
        </slot>
      </div>
    </div>
    <Table
      ref="tableRef"
      bordered
      :columns="_columns"
      :postParams="postParams"
      :getParams="getParams"
      :rowSelection="_rowSelection"
      :row-key="rowKey"
      v-bind="$attrs"
    >
      <template #bodyCell="scope: Scope">
        <template v-if="scope.column">
          <slot v-if="scope.column.dataIndex === OPERATION" name="operation" v-bind="scope">
            <div class="operation">
              <template v-if="isCurrentRow(scope.record, scope.index)">
                <Button type="link" @click="saveEditRecord()">保存</Button>
                <Button type="link" danger @click="cancelEditRecord()">取消</Button>
              </template>
              <template v-else>
                <Button v-if="isCatRecord" type="link" @click="catRecord(scope)">查看</Button>
                <Button v-if="isRowEdit" type="link" @click="editRecord(scope)">编辑</Button>
                <Popconfirm title="确定删除吗?" ok-text="确认" cancel-text="取消" @confirm="delRecord(scope)">
                  <Button v-if="isRowEdit" type="link" style="color: #ff0000">删除</Button>
                </Popconfirm>

                <slot name="operation-btn"></slot>
              </template>
            </div>
          </slot>

          <slot v-else-if="scope.column.dataIndex === SERIAL_NUMBER" name="serialNumber" v-bind="scope">{{
            scope.serialNumber
          }}</slot>

          <template v-else-if="isCurrentRow(scope.record, scope.index) && scope.column.edit !== false && isRowEdit">
            <Spin :spinning="recordLoading">
              <FormItem :field="getFormFiled(scope)" :model="editData.record" />
            </Spin>
          </template>

          <component :is="scope.column?.bodyCell(scope)" v-else-if="scope.column?.bodyCell" />

          <template v-else-if="scope.column.tag">
            <Tag v-for="(val, i) in getViewVale(scope.column, scope.record)" :key="i">{{ val }} </Tag>
          </template>

          <template v-else-if="scope.column.enum">
            <span v-for="(val, i) in getViewVale(scope.column, scope.record)" :key="i">{{ val }}</span>
          </template>

          <template v-else-if="scope.column.dateFormat">
            <span>{{
              scope.text
                ? dayjs(scope.text).format(
                    scope.column.dateFormat === true ? 'YYYY-MM-DD HH:mm:ss' : scope.column.dateFormat
                  )
                : ''
            }}</span>
          </template>

          <slot v-else name="bodyCell" v-bind="scope"> {{ scope.text }}</slot>
        </template></template
      >

      <template #headerCell="scope: { column: ColumnProps<RecordType>, title: string }">
        <template v-if="scope.column">
          <component :is="scope.column?.headerCell(scope)" v-if="scope.column?.headerCell" />
          <slot v-else name="headerCell" v-bind="scope" />
        </template>
      </template>
    </Table>
  </div>
  <Drawer v-model:visible="columnSettings" title="列设置" placement="right">
    <Table ref="columnTableRef" bordered :data-source="originalColumn" :pagination="false" :scroll="{}">
      <TableColumn title="拖拽" width="80px" align="center">
        <DragOutlined style="cursor: move" />
      </TableColumn>
      <TableColumn title="列名" data-index="title" align="center"></TableColumn>
      <TableColumn title="显示"  align="center">
        <template #default="{ record, index }">
          <Switch
            :checked="_columns.some(e => e.dataIndex === record.dataIndex || e.key === record.key)"
            @change="columnSettingsChange($event, record, index)"
          />
        </template>
      </TableColumn>
    </Table>
  </Drawer>
</template>

<style lang="less" scoped>
.table-search {
  border: 1px #dfdfdf69 solid;
  padding: 24px 10px 0 10px;
  border-radius: 2px;
}

.table-main {
  border: 1px #dfdfdf69 solid;
  padding: 10px;
  border-radius: 2px;
  margin-top: 10px;

  .table-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;

    .header-button-lf,
    .header-button-ri {
      display: flex;
      gap: 10px;
    }
  }

  .operation {
    white-space: nowrap;
  }
}
</style>
